import showCommonProjectSwitch from '@/components/widgets/project-switch';
import i18n from '@/i18n';
import router from '@/router';
import type { SiteMenuItem, SiteRouteItem } from '@/router/types';
import type { User } from '@/types/common';
import { PermissionPageKey } from '@/types/permission';
import { iconUtil } from '@/utils';
import { defineStore } from 'pinia';
import { computed } from 'vue';
import type { Router } from 'vue-router';
import { useUserStore } from './user';

// 获取所有 site 的 route 配置信息
const siteRoutes: SiteRouteItem[] = Object.values(import.meta.glob('/src/sites/*/route/index.ts', { eager: true })).map(
  (e: any) => e.default,
);

// 工作台/导航排序：售后反馈管理、售后质量管理 固定排最后（其余保持 glob 顺序，sort 稳定）
const SITE_ORDER_LAST: PermissionPageKey[] = [PermissionPageKey.AfterSale, PermissionPageKey.AfterSaleQuality];
siteRoutes.sort((a, b) => {
  const al = SITE_ORDER_LAST.includes(a.name) ? 1 : 0;
  const bl = SITE_ORDER_LAST.includes(b.name) ? 1 : 0;
  return al - bl;
});

export const useAppStore = defineStore('app', () => {
  // 所有 site 的 page key
  const allSiteKeys = getAllSitePageKeys(siteRoutes);

  // 用户 store，获取当前用户信息 & 权限信息
  const us = useUserStore();

  /**
   * @description 初始化权限和路由列表
   */
  function initial(router: Router) {
    injectSiteRoutesToGlobalRouter(router, siteRoutes);
  }

  /**
   * @description 当前用户拥有权限的 siteRouteItem 列表
   */
  const siteList = computed(() => {
    return siteRoutes.filter((item) => hasUserPermission(item, us.current, us.pagePermissions));
  });

  /**
   * @description 当前页面的 siteRouteItem
   */
  const currentSite = computed(() => {
    // 使用 router.currentRoute.value 而不是 useRoute()，避免在 store 中的响应性问题
    const route = router.currentRoute.value;
    // 添加空值检查，防止路由切换时 route.matched 为空导致报错
    if (!route.matched || route.matched.length === 0) {
      return undefined;
    }
    const pageKey = route.matched[0]?.name;
    if (!pageKey) {
      return undefined;
    }
    const site = siteRoutes.find((item) => item.name === pageKey);
    return site;
  });

  /**
   * @description 当前 site 的菜单内容
   */
  const currentSiteMenus = computed(() => {
    if (!currentSite.value) {
      return [];
    }
    const m = siteRouteItemToMenu(currentSite.value, us.current, us.pagePermissions);
    if (!m) {
      return [];
    }
    return m.children;
  });

  /**
   * @description 前往指定 site 首页
   */
  function gotoSite(name: PermissionPageKey) {
    if (name === PermissionPageKey.Project) {
      // 前往项目空间，需要先选择项目
      showCommonProjectSwitch({
        onProjectEntry: (project) => {
          router.push({
            name: PermissionPageKey.ProjectDashboard,
            params: { pkey: project.pkey },
          });
        },
      });
      return;
    }
    router.push({ name });
  }

  /**
   * @description Page Key 是否在 siteRoutes 内
   */
  function isPageKeyInSites(name: PermissionPageKey) {
    return allSiteKeys.includes(name);
  }

  /**
   * @description 获取所有 site route
   */
  function getAllSiteRoutes() {
    return siteRoutes;
  }

  return {
    initial,
    siteList,
    currentSite,
    currentSiteMenus,
    gotoSite,
    isPageKeyInSites,
    getAllSiteRoutes,
  };
});

/**
 * @description 动态注入到全局路由
 */
function injectSiteRoutesToGlobalRouter(router: Router, siteRoutes: SiteRouteItem[]) {
  for (const item of siteRoutes) {
    router.addRoute(item as any);
  }
}

/**
 * @description 判断当前用户是否有权限访问 siteRouteItem
 */
function hasUserPermission(item: SiteRouteItem, currentUser: User, pagePermissions: string[]) {
  if (!currentUser) {
    return false;
  }
  // 并集：拥有任一命中角色即可见（复权即生效，全站直接显示，无需切换视角）
  const roleIds = currentUser.roles.map((r) => r.id);
  if (item.meta.roles && !item.meta.roles.some((r) => roleIds.includes(r))) {
    return false;
  }
  if (!pagePermissions.includes(item.name)) {
    return false;
  }
  return true;
}

/**
 * @description siteRouteItem 转换成 siteRouteMenuItem
 */
function siteRouteItemToMenu(item: SiteRouteItem, currentUser: User, pagePermissions: string[]) {
  if (!currentUser) {
    return null;
  }
  // 并集：菜单项按"拥有任一命中角色"过滤（复权即显示）
  const roleIds = currentUser.roles.map((r) => r.id);
  const menuItem: SiteMenuItem = {
    label: i18n.global.t(item.meta.title),
    key: item.name,
    icon: item.meta.icon
      ? iconUtil.renderIconWithProps({
          icon: item.meta.icon,
          class: item.meta.iconCls,
        })
      : undefined,
    hideInSoftwareProject: item.meta.hideInSoftwareProject,
    children: item.children
      ?.filter(
        (e) =>
          !e.meta.hideInMenu &&
          pagePermissions.includes(e.name) &&
          (!e.meta.roles || e.meta.roles.some((r) => roleIds.includes(r))),
      )
      .map((e) => siteRouteItemToMenu(e, currentUser, pagePermissions)),
  };
  return menuItem;
}

/**
 * @description 从 site 内获取所有的 page key
 */
function getAllSitePageKeys(siteRoutes: SiteRouteItem[]) {
  const keys: string[] = [];
  siteRoutes.forEach((r) => {
    if (r.name) {
      keys.push(r.name);
    }
    if (r.children) {
      keys.push(...getAllSitePageKeys(r.children));
    }
  });
  return keys;
}
